/**
 * AvatarCropModal — interactive square crop editor for staff profile photos.
 *
 * Flow:
 *   1. Parent picks an image file → opens this modal with `imageSrc` set.
 *   2. User drags to reposition + uses the zoom slider.
 *   3. On "Save", we crop client-side to a 512×512 PNG Blob, then handoff to
 *      `onSave(blob)` which uploads to the backend. Backend still resizes to
 *      256×256 + strips EXIF as a defence-in-depth step.
 *
 * Uses react-easy-crop for the canvas drag + pinch interactions. Renders the
 * final cropped pixels via a hidden <canvas>.
 */
import { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Loader2, RotateCcw, ZoomIn, ZoomOut } from "lucide-react";

// ---------- helpers ----------
async function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });
}

async function cropImageToBlob(imageSrc, pixelCrop) {
  const img = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  const OUT = 512; // upload at 512×512; backend will resize to 256×256.
  canvas.width = OUT;
  canvas.height = OUT;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    img,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, OUT, OUT,
  );
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("canvas-to-blob failed"));
    }, "image/png", 0.95);
  });
}

// ---------- component ----------
export default function AvatarCropModal({ open, imageSrc, onClose, onSave, busy = false }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedPixels, setCroppedPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  // Reset when a new image is loaded.
  useEffect(() => {
    if (open) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedPixels(null);
    }
  }, [open, imageSrc]);

  const onCropComplete = useCallback((_area, areaPx) => {
    setCroppedPixels(areaPx);
  }, []);

  const handleSave = async () => {
    if (!croppedPixels || !imageSrc) return;
    setSaving(true);
    try {
      const blob = await cropImageToBlob(imageSrc, croppedPixels);
      await onSave(blob);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  // Track dirty state purely for the busy/save flow — the shared
  // <DialogContent /> handles the close-confirmation universally now.
  const dirty = (zoom !== 1 || crop.x !== 0 || crop.y !== 0) && !saving && !busy;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-md max-h-[90vh] overflow-y-auto"
        data-testid="avatar-crop-modal"
        dirtyGuardActive={() => dirty}
        dirtyGuardMessage="Discard this cropped photo and close?"
      >
        <DialogTitle className="text-lg font-semibold">Crop your profile photo</DialogTitle>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Drag to reposition · use the slider to zoom · save when it looks right.</p>

        {/* Crop canvas */}
        <div className="relative w-full mt-4 bg-slate-900 rounded-xl overflow-hidden" style={{ height: 320 }}>
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              objectFit="contain"
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>

        {/* Zoom slider */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 mb-2">
            <span className="inline-flex items-center gap-1"><ZoomOut className="w-3 h-3" /> Zoom</span>
            <span className="inline-flex items-center gap-1"><ZoomIn className="w-3 h-3" /></span>
          </div>
          <Slider
            value={[zoom]}
            min={1}
            max={4}
            step={0.05}
            onValueChange={(vals) => setZoom(vals[0])}
            data-testid="avatar-zoom-slider"
          />
        </div>

        <div className="flex items-center justify-between gap-2 mt-5">
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            disabled={saving || busy}
            data-testid="avatar-crop-reset"
          >
            <RotateCcw className="w-4 h-4 mr-1" /> Reset
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={saving || busy}
              data-testid="avatar-crop-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={!croppedPixels || saving || busy}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="avatar-crop-save"
            >
              {saving || busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Save photo
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
